'use client';

import React from 'react';

interface Column<T> {
  header: string;
  accessor: keyof T | ((item: T) => React.ReactNode);
  className?: string;
}

interface AdminTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  onRowClick?: (item: T) => void;
}

export function AdminTable<T extends { id: string | number }>({ 
  columns, 
  data, 
  loading, 
  emptyMessage = "No records found.",
  onRowClick
}: AdminTableProps<T>) {
  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-nature-muted/10">
            {columns.map((col, idx) => (
              <th 
                key={idx} 
                className={`text-left py-5 px-6 text-[10px] font-black uppercase tracking-[0.2em] text-moss/60 ${col.className || ''}`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-nature-muted/5">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className="animate-pulse">
                {columns.map((_, j) => (
                  <td key={j} className="py-6 px-6">
                    <div className="h-4 bg-nature-muted/10 rounded-full w-full" />
                  </td>
                ))}
              </tr>
            ))
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="py-20 text-center">
                <p className="text-sm font-medium text-moss/50 italic">{emptyMessage}</p>
              </td>
            </tr>
          ) : (
            data.map((item) => (
              <tr 
                key={item.id} 
                onClick={() => onRowClick?.(item)}
                className={`group transition-colors duration-200 ${onRowClick ? 'cursor-pointer hover:bg-bg/50' : ''}`}
              >
                {columns.map((col, idx) => (
                  <td key={idx} className={`py-6 px-6 text-sm font-medium text-soot ${col.className || ''}`}>
                    {typeof col.accessor === 'function' 
                      ? col.accessor(item) 
                      : (item[col.accessor] as React.ReactNode)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
